import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartLine, ModifierSelection } from '@/types/domain';
import { createIdbStorage } from './persist';

export const MAX_QTY = 99;
export const MIN_QTY = 1;

function computeLineTotal(basePrice: number, modifiers: ModifierSelection[], qty: number): number {
  const unit = basePrice + modifiers.reduce((sum, m) => sum + m.priceDelta, 0);
  return unit * qty;
}

type CartInput = Omit<CartLine, 'lineId' | 'lineTotal'>;

type CartState = {
  lines: CartLine[];
  add: (line: CartInput) => string;
  updateQty: (lineId: string, qty: number) => void;
  remove: (lineId: string) => void;
  clear: () => void;
  subtotal: () => number;
  itemCount: () => number;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],

      add: (input) => {
        const lineId = crypto.randomUUID();
        const lineTotal = computeLineTotal(input.basePrice, input.modifiers, input.qty);
        set((s) => ({ lines: [...s.lines, { ...input, lineId, lineTotal }] }));
        return lineId;
      },

      updateQty: (lineId, qty) => {
        const clamped = Math.min(MAX_QTY, Math.max(MIN_QTY, qty));
        set((s) => ({
          lines: s.lines.map((l) =>
            l.lineId === lineId
              ? { ...l, qty: clamped, lineTotal: computeLineTotal(l.basePrice, l.modifiers, clamped) }
              : l,
          ),
        }));
      },

      remove: (lineId) => set((s) => ({ lines: s.lines.filter((l) => l.lineId !== lineId) })),

      clear: () => set({ lines: [] }),

      subtotal: () => get().lines.reduce((sum, l) => sum + l.lineTotal, 0),

      itemCount: () => get().lines.reduce((sum, l) => sum + l.qty, 0),
    }),
    {
      name: 'hanami-cart',
      storage: createIdbStorage(),
      partialize: (s) => ({ lines: s.lines }) as CartState,
    },
  ),
);
